'use client';

import React from 'react';

import { IconChevronRight } from '@icons';

export interface CardImageDetailsProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  /** When set, the root renders as `<a>` (whole card is one link). */
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  /** Recommended when **`href`** is set (e.g. “Preview 1g Gold Bar”). */
  'aria-label'?: string;
  cardImage: string;
  cardImage2x?: string;
  cardImageAlt: string;
  /** `<img width>` / `<img height>` for layout stability (defaults 320). */
  imageWidth?: number;
  imageHeight?: number;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Optional grade/spec pill (e.g. PR-70). */
  grade?: React.ReactNode;
  /** Uppercase label above **`marketValue`** (default: “Market value”). */
  marketLabel?: string;
  marketValue: React.ReactNode;
  /** Decorative trailing glyph (default: chevron right). */
  trailingIcon?: React.ReactNode;
}

const DEFAULT_MARKET_LABEL = 'Market value';

const CardImageDetails = React.forwardRef<HTMLAnchorElement | HTMLDivElement, CardImageDetailsProps>(
  (props, ref) => {
    const {
      href,
      target,
      rel,
      'aria-label': ariaLabel,
      cardImage,
      cardImage2x,
      cardImageAlt,
      imageWidth = 320,
      imageHeight = 320,
      title,
      subtitle,
      grade,
      marketLabel = DEFAULT_MARKET_LABEL,
      marketValue,
      trailingIcon,
      className = '',
      ...rest
    } = props;

    const rootClass = ['card-image-details', href ? 'card-image-details-link' : '', className]
      .filter(Boolean)
      .join(' ')
      .trim();

    const srcSet = cardImage2x ? `${cardImage2x} 2x` : undefined;
    const showGrade = grade != null && grade !== '';

    const shell = (
      <div className='card-image-details-shell'>
        <div className='card-image-details-media'>
          {/* Native img: arbitrary URLs plus optional 2x `srcSet` (next/image cannot express that pair). */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            className='card-image-details-img'
            src={cardImage}
            srcSet={srcSet}
            alt={cardImageAlt}
            width={imageWidth}
            height={imageHeight}
            loading='lazy'
            decoding='async'
          />
        </div>
        <div className='card-image-details-body'>
          <div className='card-image-details-header'>
            <div className='card-image-details-title-block'>
              <h3 className='card-image-details-title'>{title}</h3>
              {subtitle ? <p className='card-image-details-subtitle'>{subtitle}</p> : null}
            </div>
            {showGrade ? <span className='card-image-details-grade'>{grade}</span> : null}
          </div>
          <div className='card-image-details-divider' aria-hidden />
          <div className='card-image-details-footer'>
            <div className='card-image-details-market'>
              <span className='card-image-details-market-label'>{marketLabel}</span>
              <span className='card-image-details-market-value'>{marketValue}</span>
            </div>
            <span className='card-image-details-chevron' aria-hidden>
              {trailingIcon ?? <IconChevronRight />}
            </span>
          </div>
        </div>
      </div>
    );

    if (href) {
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          target={target}
          rel={rel}
          aria-label={ariaLabel}
          className={rootClass}
          {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {shell}
        </a>
      );
    }

    return (
      <div ref={ref as React.Ref<HTMLDivElement>} className={rootClass} {...rest}>
        {shell}
      </div>
    );
  }
);

CardImageDetails.displayName = 'CardImageDetails';

export default CardImageDetails;
